import { AgeGenderDialogStep } from '@/components/age-and-gender/dialog';
import useAgeGenderDialog from '@/hooks/use-age-gender-dialog';
import { SkipDeleteRedirectQueryKey } from '@rhg/components/delete-account/types';
import useAuthRedirect from '@rhg/hooks/use-auth-redirect';
import { usePreviousRegistrationFlow } from '@rhg/hooks/use-previous-registration-flow';
import {
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import {
    RegistrationDialogOpenQueryKey,
    useRegistrationDialog,
} from '@rhg/hooks/use-registration-dialog';
import { useUserBlocker } from '@rhg/hooks/use-user-blocker';
import { UserBlocker } from '@rhg/hooks/use-user-blocker/types';
import useAuth from '@rhg/libs/auth0/use-auth';
import { Box } from '@rhg/libs/material';
import { routes } from '@rhg/utils/routes';
import { getIsInitialSSO } from '@rhg/utils/sso';
import omit from 'lodash/omit';
import { useRouter } from 'next/router';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { SkipLinkingSSOQueryKey } from '../registration/linking-sso-step/types';
import { FullWidthLinearProgress } from '../ui/progress';

const styleHidden = { display: 'none' };

export const UserBlockerGate = ({ children }: PropsWithChildren) => {
    const {
        isLoading: isPreviousRegistrationFlowLoading,
        previousRegistrationFlow,
    } = usePreviousRegistrationFlow();
    const { isRegistrationDialogOpen } = useRegistrationDialog();
    const { pathname, query, replace, isReady: routerIsReady } = useRouter();
    const { isLoading: isAuthLoading, isAuthenticated } = useAuth();
    const { redirect } = useAuthRedirect();
    const { userBlocker, isLoading: isUserBlockerLoading } = useUserBlocker();
    const isSSO = getIsInitialSSO();

    const [hasFinished, setHasFinished] = useState(false);
    const {
        setOpenStep: setAgeGenderDialogOpenStep,
        isOpen: isAgeGenderDialogOpen,
    } = useAgeGenderDialog();

    // This is to check the requirements for whether the onboarding check should fire or not
    const isLoaded = useMemo(() => {
        if (isAuthLoading || !routerIsReady) {
            return false;
        }

        if (
            !isAuthenticated ||
            (!isUserBlockerLoading && !isPreviousRegistrationFlowLoading)
        ) {
            return true;
        }

        return false;
    }, [
        isAuthLoading,
        isAuthenticated,
        isPreviousRegistrationFlowLoading,
        isUserBlockerLoading,
        routerIsReady,
    ]);

    const isOnExcludedPage = useMemo(() => {
        if (pathname === routes.auth) {
            return true;
        }
        if (pathname.includes('/components')) {
            return true;
        }

        if (pathname === routes.dashboard && !isAuthenticated) {
            return true;
        }

        return [
            routes.error,
            routes.expired,
            routes.notFound,
            routes.login,
            routes.reconsent,
            routes.standaloneResults,
            routes.standaloneScreening,
            routes.nonUsResident,
            routes.signup,
            routes.saml,
            routes.sso,
            routes.ssoInfo,
            routes.registration,
            routes.programDetails(),
        ].includes(pathname);
    }, [isAuthenticated, pathname]);

    const needsAuthRedirect = useMemo(
        () => isLoaded && pathname === routes.auth,
        [isLoaded, pathname]
    );

    const needsUserBlockerHandling = useMemo(
        () =>
            isLoaded &&
            !query[SkipDeleteRedirectQueryKey] &&
            !isOnExcludedPage &&
            !!userBlocker &&
            [
                ...(query[SkipLinkingSSOQueryKey]
                    ? []
                    : [UserBlocker.LinkingSSO]),
                UserBlocker.Expired,
                UserBlocker.CountryUnsponsored,
                UserBlocker.Tos,
                UserBlocker.CCHDUnsponsored,
                UserBlocker.Reconsent,
                UserBlocker.ReceivingInvitation,
                // These are post-sponsorship blockers, if we are already in the registration dialog flow, they should be ignored.
                // If the user exits the flow they'll show
                ...(isRegistrationDialogOpen
                    ? []
                    : [
                          UserBlocker.CountrySponsored,
                          UserBlocker.CCHDSponsored,
                          UserBlocker.RegistrationAgeGender,
                          UserBlocker.UK,
                          UserBlocker.CommunicationsPreference,
                          UserBlocker.RegistrationParentalConsent,
                      ]),
                ...(isRegistrationDialogOpen || isAgeGenderDialogOpen
                    ? []
                    : [UserBlocker.StandaloneParentalConsent]),
            ].includes(userBlocker),
        [
            isAgeGenderDialogOpen,
            isLoaded,
            isOnExcludedPage,
            isRegistrationDialogOpen,
            query,
            userBlocker,
        ]
    );

    /**
     * This effect makes sure that users are go through the onboarding flow correctly.
     * For now the FTUE flow is largely unmanaged by BE state, this will be done later.
     * Instead the user will be able to move around freely instead of being forced into the flow
     */
    useEffect(() => {
        if (!isLoaded) {
            return;
        }

        /**
         * If the user lands on the /auth page and has a redirect, send them to it.
         */
        if (needsAuthRedirect) {
            if (isSSO && !userBlocker) {
                /*
                 ** If the user is coming in through SSO and has no registration step assigned, send them to the dashboard
                 ** This can occur when the SSO user is already sponsored by a related parent or child partner
                 */
                replace(routes.dashboard);
            } else if (redirect) {
                replace(redirect);
            } else {
                replace(routes.dashboard, { query });
            }
            setHasFinished(true);
            return;
        }

        /**
         * If the user does not have a first name, or has not accepted TOS, start registration
         */
        if (needsUserBlockerHandling) {
            switch (userBlocker) {
                case UserBlocker.LinkingSSO:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            currentStep: RegistrationFlowStep.LinkingSSO,
                        },
                    });
                    break;
                case UserBlocker.ReceivingInvitation:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            registrationFlow:
                                RegistrationFlow.NewInviteeRegistration,
                            currentStep:
                                RegistrationFlowStep.ReceivingInvitation,
                        },
                    });
                    break;
                case UserBlocker.Tos:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            currentStep: RegistrationFlowStep.CreateCircle,
                        },
                    });
                    break;
                case UserBlocker.Expired:
                    replace({
                        pathname: routes.expired,
                        // We want to remove rdo from the query since it's handled separately
                        query: omit(query, [RegistrationDialogOpenQueryKey]),
                    });
                    break;
                case UserBlocker.CountryUnsponsored:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            currentStep: RegistrationFlowStep.AccessCode,
                            registrationFlow: RegistrationFlow.NewRegistration,
                        },
                    });
                    break;
                case UserBlocker.CountrySponsored:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            isPostEligibility: true,
                            currentStep: RegistrationFlowStep.SelectCountry,
                            // We are post-sponsorship so we need to know how we were getting sponsorship before so we can display the right success screen
                            registrationFlow:
                                previousRegistrationFlow ??
                                RegistrationFlow.NewRegistration,
                        },
                    });
                    break;
                case UserBlocker.CommunicationsPreference:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            isPostEligibility: true,
                            currentStep:
                                RegistrationFlowStep.EmailCommunicationPreferences,
                            // We are post-sponsorship so we need to know how we were getting sponsorship before so we can display the right success screen
                            registrationFlow:
                                previousRegistrationFlow ??
                                RegistrationFlow.NewRegistration,
                        },
                    });
                    break;
                case UserBlocker.CCHDUnsponsored:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            currentStep: RegistrationFlowStep.CCHDConsent,
                            registrationFlow: RegistrationFlow.SkipSponsorship,
                        },
                    });
                    break;
                case UserBlocker.CCHDSponsored:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            isPostEligibility: true,
                            currentStep: RegistrationFlowStep.CCHDConsent,
                            // We are post-sponsorship so we need to know how we were getting sponsorship before so we can display the right success screen
                            registrationFlow:
                                previousRegistrationFlow ??
                                RegistrationFlow.NewRegistration,
                        },
                    });
                    break;
                case UserBlocker.RegistrationAgeGender:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            isPostEligibility: true,
                            currentStep: RegistrationFlowStep.AgeGender,
                            // We are post-sponsorship so we need to know how we were getting sponsorship before so we can display the right success screen
                            registrationFlow:
                                previousRegistrationFlow ??
                                RegistrationFlow.NewRegistration,
                        },
                    });
                    break;
                case UserBlocker.UK:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            isPostEligibility: true,
                            currentStep: RegistrationFlowStep.CountryConsent,
                            // We are post-sponsorship so we need to know how we were getting sponsorship before so we can display the right success screen
                            registrationFlow:
                                previousRegistrationFlow ??
                                RegistrationFlow.NewRegistration,
                        },
                    });
                    break;
                case UserBlocker.RegistrationParentalConsent:
                    replace({
                        pathname: routes.registration,
                        query: {
                            ...query,
                            isPostEligibility: true,
                            currentStep: RegistrationFlowStep.ParentalConsent,
                            // We are post-sponsorship so we need to know how we were getting sponsorship before so we can display the right success screen
                            registrationFlow:
                                previousRegistrationFlow ??
                                RegistrationFlow.NewRegistration,
                        },
                    });
                    break;
                case UserBlocker.Reconsent:
                    replace({
                        pathname: routes.reconsent,
                        query: {
                            ...query,
                        },
                    });
                    break;
                case UserBlocker.StandaloneParentalConsent:
                    setAgeGenderDialogOpenStep(
                        AgeGenderDialogStep.ParentalConsent
                    );
                    break;
            }

            setHasFinished(true);
            return;
        }

        setHasFinished(true);
    }, [
        isLoaded,
        isOnExcludedPage,
        isSSO,
        needsAuthRedirect,
        needsUserBlockerHandling,
        pathname,
        previousRegistrationFlow,
        query,
        redirect,
        replace,
        setAgeGenderDialogOpenStep,
        userBlocker,
    ]);

    const isReady =
        isLoaded &&
        hasFinished &&
        !needsAuthRedirect &&
        !needsUserBlockerHandling;

    const shouldShowBecauseLinkingSSO =
        userBlocker === UserBlocker.LinkingSSO && query[SkipLinkingSSOQueryKey];

    const shouldShowBecauseUnsponsored =
        userBlocker === UserBlocker.Unsponsored;

    const shouldShowContent =
        isOnExcludedPage ||
        ((!userBlocker ||
            shouldShowBecauseLinkingSSO ||
            shouldShowBecauseUnsponsored) &&
            hasFinished);
    const ariaProps = shouldShowContent ? {} : { 'aria-hidden': true };

    return (
        <>
            {!isReady && <FullWidthLinearProgress />}
            <Box
                sx={shouldShowContent ? undefined : styleHidden}
                {...ariaProps}
            >
                {children}
            </Box>
        </>
    );
};
